import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { searchIntakesAPI } from "../../service/intakes";

/**
 * Intakes that match the Matters search — clients who have not signed yet.
 *
 * Shown only while a search term is typed (the default list stays matters
 * only), above the matter rows and visibly subordinate to them: a thin
 * strip, one line per intake, the "Intake · not signed" badge and the
 * document count. Signed intakes never appear (their matter is the file);
 * turned-down ones fade 60 days after the turn-down unless "include
 * closed" is switched on. Clicking opens the intake file.
 *
 * It can never take over the page: at most MAX_ROWS intakes are listed,
 * the rest is one line — "+N more — keep typing to narrow" (Matthew,
 * 2026-10-30, on a surname search that listed nineteen: "this is what we
 * were trying to avoid"). The server matches every typed word, so
 * "narciso martinez" is Narciso alone, not every Martinez.
 *
 * And it starts closed. What the page shows is one thin line — "View
 * Intake Results (N)" — and the list opens only when someone clicks it
 * (Matthew, 2026-11-02, on a two-letter search that still showed five
 * prospects above his matters: "I really don't want to see any intake
 * stuff … leave the intake box as a collapsed drop down. Thin line up
 * top 'View Intake Results'"). Once opened it stays open while the person
 * keeps narrowing the same search; leaving the page closes it again.
 */
export const MAX_ROWS = 5;
export const MIN_CHARS = 2;
export const COLLAPSED_LABEL = "View Intake Results";

const Bar = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  margin: 0 0 10px 0;
  padding: 5px 10px;
  border: 1px solid #e3e7ee;
  border-radius: 6px;
  background: ${(p) => (p.$open ? "#fffaf0" : "#fafbfc")};
  border-color: ${(p) => (p.$open ? "#f0d9a8" : "#e3e7ee")};
  color: ${(p) => (p.$open ? "#6b4e00" : "#5a6678")};
  font-family: 'Manrope', sans-serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  text-align: left;
  cursor: pointer;
  &:hover { background: ${(p) => (p.$open ? "#fff1d6" : "#f1f3f7")}; }
`;

const Chevron = styled.span`
  display: inline-block;
  width: 10px;
  font-size: 10px;
  transition: transform 0.15s ease;
  transform: rotate(${(p) => (p.$open ? 90 : 0)}deg);
`;

const Strip = styled.section`
  margin: 0 0 14px 0;
  padding: 10px 14px;
  border: 1px solid #f0d9a8;
  background: #fffaf0;
  border-radius: 8px;
  font-family: 'Manrope', sans-serif;
`;

const Head = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  color: #6b4e00;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
`;

const Toggle = styled.button`
  border: 0;
  background: none;
  color: #0c53bb;
  font: inherit;
  font-size: 12px;
  text-transform: none;
  letter-spacing: 0;
  cursor: pointer;
  padding: 0;
  &:hover { text-decoration: underline; }
`;

const Rows = styled.ul`
  list-style: none;
  margin: 8px 0 0 0;
  padding: 0;
`;

const Row = styled.li`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  gap: 16px;
  align-items: center;
  padding: 7px 8px;
  border-radius: 6px;
  cursor: pointer;
  color: #1b2028;
  font-size: 14px;
  &:hover { background: #fff1d6; }
`;

const Name = styled.span`
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CaseType = styled.span`
  color: #48566b;
  font-weight: 500;
  margin-left: 8px;
`;

const Badge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  background: ${(p) => (p.$closed ? "#eef0f4" : "#fde9c2")};
  color: ${(p) => (p.$closed ? "#5a6678" : "#7a4f00")};
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
`;

const Count = styled.span`
  color: #48566b;
  font-size: 13px;
  white-space: nowrap;
  min-width: 110px;
  text-align: right;
`;

const Empty = styled.div`
  margin-top: 6px;
  color: #6b7280;
  font-size: 13px;
`;

const More = styled.div`
  margin: 6px 8px 0 8px;
  color: #6b4e00;
  font-size: 12px;
`;

export function documentCountLabel(count) {
  if (!count) return "no documents yet";
  return count === 1 ? "1 document" : `${count} documents`;
}

const IntakeStrip = ({ search }) => {
  const navigate = useNavigate();
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [includeClosed, setIncludeClosed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  // Closed by default; a click opens it and it stays open while the same
  // search is being narrowed. The component unmounts with the Matters page,
  // so the next visit starts closed again.
  const [open, setOpen] = useState(false);
  const term = (search || "").trim();

  useEffect(() => {
    let cancelled = false;
    if (term.length < MIN_CHARS) {
      setResults([]);
      setTotal(0);
      setLoaded(false);
      return undefined;
    }
    searchIntakesAPI(term, { includeClosed, limit: MAX_ROWS })
      .then((response) => {
        if (cancelled) return;
        const rows = response?.data?.results || [];
        setResults(rows);
        setTotal(Number.isFinite(response?.data?.total) ? response.data.total : rows.length);
        setLoaded(true);
      })
      .catch(() => {
        // The strip is an addition to the matter list; a failure here must
        // never take the matters with it.
        if (cancelled) return;
        setResults([]);
        setTotal(0);
        setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [term, includeClosed]);

  if (term.length < MIN_CHARS || !loaded) return null;
  if (!results.length && !includeClosed) return null;

  const shown = results.slice(0, MAX_ROWS);
  const hidden = Math.max(0, total - shown.length);

  // One tree in both states, so toggling never remounts the button (a
  // remount drops keyboard focus mid-toggle); only the box comes and goes.
  return (
    <>
      <Bar
        type="button"
        $open={open}
        aria-expanded={open}
        aria-controls="intake-results"
        data-testid="intake-strip-bar"
        onClick={() => setOpen((v) => !v)}
      >
        <Chevron $open={open} aria-hidden="true">▶</Chevron>
        <span>{open ? "Intake Results" : COLLAPSED_LABEL} ({total})</span>
      </Bar>
      {open ? (
        <Strip id="intake-results" data-testid="intake-strip" aria-label="Intakes not yet signed">
          <Head>
            <span>Intakes · not signed ({total})</span>
            <Toggle type="button" onClick={() => setIncludeClosed((v) => !v)} data-testid="intake-strip-toggle">
              {includeClosed ? "Hide closed intakes" : "Include closed intakes"}
            </Toggle>
          </Head>
          {shown.length ? (
            <Rows>
              {shown.map((intake) => (
                <Row
                  key={intake.id}
                  data-testid="intake-row"
                  onClick={() => navigate(`/intake/${intake.id}`)}
                >
                  <Name title={intake.display_name}>
                    {intake.client_name || intake.display_name}
                    {intake.case_type ? <CaseType>{intake.case_type}</CaseType> : null}
                  </Name>
                  <Badge $closed={intake.intake_kind === "closed"}>{intake.badge}</Badge>
                  <Count>{documentCountLabel(intake.document_count)}</Count>
                </Row>
              ))}
            </Rows>
          ) : (
            <Empty>No closed intakes match either.</Empty>
          )}
          {hidden > 0 ? (
            <More data-testid="intake-strip-more">
              +{hidden} more — keep typing to narrow
            </More>
          ) : null}
        </Strip>
      ) : null}
    </>
  );
};

export default IntakeStrip;
